/**
 * Utility per la navigazione e geocodifica Google Maps
 * degli impianti semaforici / stradali.
 */

export interface ImpiantoMapsTarget {
  nome?: string | null;
  lat?: number | string | null;
  lng?: number | string | null;
  indirizzo?: string | null;
}

/**
 * Normalizza il nome dell'impianto semaforico estraendo le vie dell'incrocio
 * ed aggiungendo il comune (Roma) per una localizzazione esatta su Google Maps.
 */
export function normalizzaIncrocioGoogleMaps(nomeImpianto?: string | null): string {
  if (!nomeImpianto) return '';
  const parti = String(nomeImpianto)
    .split(/\s*-\s*|\s*\/\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parti.length >= 2) {
    return `${parti[0]}, ${parti[1]}, Roma`;
  }
  return `${String(nomeImpianto).trim()}, Roma`;
}

/**
 * Calcola l'URL universale di Google Maps per un impianto o coordinate.
 * Se latitudine e longitudine sono presenti e valide, punta alle coordinate esatte WGS84.
 * Altrimenti, esegue il fallback sul nome/incrocio normalizzato.
 */
export function getGoogleMapsUrl(
  target?: ImpiantoMapsTarget | string | number | null,
  lng?: number | string | null,
  nomeFallback?: string | null
): string | null {
  if (!target && !lng) return null;

  let finalLat: any = null;
  let finalLng: any = null;
  let finalNome: string | null = null;

  if (typeof target === 'object' && target !== null) {
    finalLat = target.lat;
    finalLng = target.lng;
    finalNome = target.nome || target.indirizzo || '';
  } else if (
    (typeof target === 'number' || (typeof target === 'string' && !isNaN(Number(target)))) &&
    lng !== undefined &&
    lng !== null &&
    !isNaN(Number(lng))
  ) {
    finalLat = target;
    finalLng = lng;
    finalNome = nomeFallback || '';
  } else if (typeof target === 'string') {
    finalNome = target;
  }

  // Se finalLat e finalLng sono valori numerici validi diversi da zero
  if (
    finalLat !== null &&
    finalLat !== undefined &&
    finalLat !== '' &&
    finalLng !== null &&
    finalLng !== undefined &&
    finalLng !== ''
  ) {
    const numLat = Number(finalLat);
    const numLng = Number(finalLng);
    if (!isNaN(numLat) && !isNaN(numLng) && (numLat !== 0 || numLng !== 0)) {
      return `https://www.google.com/maps/search/?api=1&query=${numLat},${numLng}`;
    }
  }

  // Fallback: se non abbiamo coordinate valide ma abbiamo un nome
  if (finalNome && typeof finalNome === 'string' && finalNome.trim() !== '') {
    const query = normalizzaIncrocioGoogleMaps(finalNome);
    if (query) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    }
  }

  return null;
}

/**
 * Apre l'impianto su Google Maps in una nuova scheda o app nativa.
 * Se disponibili, utilizza latitudine e longitudine; altrimenti fa fallback sul nome incrocio.
 */
export function apriGoogleMaps(
  target?: ImpiantoMapsTarget | string | number | null,
  lng?: number | string | null,
  nomeFallback?: string | null
): string | null {
  const url = getGoogleMapsUrl(target, lng, nomeFallback);
  if (!url) return null;
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  return url;
}

/**
 * Apre la panoramica immersiva a 360° di Google Street View in una finestra
 * pop-up fluttuante centrata in primo piano sullo schermo (senza API a pagamento).
 */
export function apriStreetViewPopup(
  lat?: number | string | null,
  lng?: number | string | null
): string | null {
  if (!lat || !lng) return null;
  const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
  if (typeof window !== 'undefined') {
    const w = 980;
    const h = 620;
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    window.open(
      url,
      'StreetViewRoadHub',
      `width=${w},height=${h},top=${top},left=${left},resizable=yes,scrollbars=no,status=no`
    );
  }
  return url;
}
